// src/app/services/ai-content.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { CardsService, DigitalCard } from './cards.service'; // Import DigitalCard

// Define these interfaces WITHIN ai-content.service.ts
export interface AIDescriptionRequest {
  cardid: string;
  name: string;
  type: 'product' | 'service';
  language?: string;
}

export interface AIDescriptionResponse {
  description: string;
}

export interface AIContentRequest {
  /** ID of the card the content belongs to */
  cardid: string;
  /** Type of content: 'product' or 'service' */
  type: 'product' | 'service';
  /** User's description of what they want to create */
  description: string;
  /** Desired language for the content (optional, defaults to English) */
  language?: string;
}

export interface ContentItem {
  /** The name of the suggested product/service */
  name: string;
  /** The description of the suggested product/service */
  description: string;
  /** Image URL for the product/service */
  image: string;
  /** Array of features for the product/service */
  features: string[];
}

export interface AIContentResponse {
  /** Array of AI-generated content suggestions */
  suggestions: ContentItem[];
  /** Raw content (fallback field) */
  content?: string;
  /** Additional sections if provided */
  sections?: any[];
}
@Injectable({
  providedIn: 'root',
})
export class AIContentService {
private readonly baseUrl = `${environment.apiUrl}/ai`;  // Use the correct endpoint
  constructor(
    private http: HttpClient, 
    private authService: AuthService,
    private cardsService: CardsService
  ) {}

  private getAuthHeaders(): HttpHeaders {
    const currentCard: DigitalCard | null = this.cardsService.selectedCard();
    if (!currentCard?.handler) {
      throw new Error('Handler not found in the selected card');
    }

    return new HttpHeaders({
      'Content-Type': 'application/json',
      'handler': currentCard.handler, // Use the handler from the selected card
    });
  }

  /**
   * Generates AI content suggestions for products or services.
   * POST /api/ai/content
   */
  generateContent(request: AIContentRequest): Observable<AIContentResponse> {
    return this.http.post<AIContentResponse>(
      `${this.baseUrl}/content`,
      request,
      { headers: this.getAuthHeaders() }
    );
  }
generateDescriptionOnly(
    cardid: string, 
    name: string, 
    type: 'product' | 'service', 
    language: string = 'English'
  ): Observable<AIDescriptionResponse> {
    return type === 'product'
      ? this.generateProductDescription(cardid, name, language)
      : this.generateServiceDescription(cardid, name, language);
  }
  /**
   * Generates AI content suggestions specifically for products.
   * POST /api/ai/content
   */
  generateProductDescription(
  cardid: string,
  name: string,
  language?: string
): Observable<AIDescriptionResponse> {
  const request: AIDescriptionRequest = {
    cardid,
    name,
    type: 'product',
    language: language || 'English'
  };

  return this.http.post<AIDescriptionResponse>(
    `${this.baseUrl}/description`,
    request,
    { headers: this.getAuthHeaders() }
  );
}

  /**
   * Generates AI content suggestions specifically for services.
   * POST /api/ai/content
   */
generateServiceDescription(
  cardid: string,
  name: string,
  language?: string
): Observable<AIDescriptionResponse> {
  const request: AIDescriptionRequest = {
    cardid,
    name,
    type: 'service',
    language: language || 'English'
  };

  return this.http.post<AIDescriptionResponse>(
   `${this.baseUrl}/description`,
    request,
    { headers: this.getAuthHeaders() }
  );
}
}